import * as React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { Box, Typography, IconButton, Stack, Button, Menu, MenuItem } from '@mui/material';

import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import StyleRoundedIcon from '@mui/icons-material/StyleRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';

import ColorSchemeToggle from './ColorSchemeToggle';
import UserMenu from './UserMenu';

const navLinks = [
  { label: 'Workspaces', to: '/workspaces', icon: <AppsRoundedIcon fontSize="small" sx={{ mr: 1 }} /> },
  { label: 'Projects', to: '/projects', icon: <StyleRoundedIcon fontSize="small" sx={{ mr: 1 }} /> },
  { label: 'Library', to: '/library', icon: <CollectionsBookmarkRoundedIcon fontSize="small" sx={{ mr: 1 }} /> },
  { label: 'Explore', to: '/explore', icon: <TravelExploreRoundedIcon fontSize="small" sx={{ mr: 1 }} /> },
  { label: 'Trips', to: '/trips', icon: <MapRoundedIcon fontSize="small" sx={{ mr: 1 }} /> },
  { label: 'Quests', to: '/quests', icon: <FlagRoundedIcon fontSize="small" sx={{ mr: 1 }} /> }
];

export default function Header({ mode, setMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const userData = useSelector((state) => state.user.userData);

  // Nav menu state
  const [navMenuAnchor, setNavMenuAnchor] = React.useState(null);
  const isNavMenuOpen = Boolean(navMenuAnchor);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  };

  // Nav menu handlers
  const handleNavMenuOpen = (event) => {
    setNavMenuAnchor(event.currentTarget);
  };
  const handleNavMenuClose = () => {
    setNavMenuAnchor(null);
  };

  // Determine the current page name based on the route
  const getPageName = () => {
    if (currentPath.startsWith('/workspaces')) return 'Workspaces';
    if (currentPath.startsWith('/projects')) return 'Projects';
    if (currentPath.startsWith('/library')) return 'Library';
    if (currentPath.startsWith('/explore')) return 'Explore';
    if (currentPath.startsWith('/trips')) return 'Trips';
    if (currentPath.startsWith('/quests')) return 'Quests';
    return 'Home';
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'space-between', alignItems: 'center' }}>
      {/* Nav menu button */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <IconButton size="medium" color="default" onClick={handleNavMenuOpen} sx={{ borderRadius: '50%' }}>
          <GridViewRoundedIcon />
        </IconButton>
        <Menu
          anchorEl={navMenuAnchor}
          open={isNavMenuOpen}
          onClose={handleNavMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {navLinks.map((link) => (
            <MenuItem key={link.to} component={Link} to={link.to} onClick={handleNavMenuClose} selected={currentPath.startsWith(link.to)}>
              {link.icon}
              {link.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          textAlign: 'center',
          pointerEvents: 'none',
          display: { xs: 'block', sm: 'none' }
        }}
      >
        <Typography variant="h6">{getPageName()}</Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', display: { xs: 'none', sm: 'flex' } }}>
        {navLinks.map((link) => (
          <Button
            key={link.to}
            variant={currentPath.startsWith(link.to) ? 'outlined' : 'text'}
            component={Link}
            to={link.to}
            sx={{ alignSelf: 'center' }}
            //            startIcon={link.icon}
          >
            {link.label}
          </Button>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center' }}>
        <ColorSchemeToggle mode={mode} setMode={setMode} />
        {userData ? (
          <UserMenu userData={userData} onLogout={handleLogout} />
        ) : (
          <Button variant="outlined" color="primary" size="small" component="a" href="/login" sx={{ alignSelf: 'center' }}>
            Login
          </Button>
        )}
      </Box>
    </Box>
  );
}
